export const PEOPLE_SEARCH_PLACEHOLDER = 'Search people by name or @handle';

export const PEOPLE_SEARCH_QUERY_MIN = 1;
export const PEOPLE_SEARCH_QUERY_MAX = 64;
export const PEOPLE_SEARCH_DEFAULT_LIMIT = 12;
export const PEOPLE_SEARCH_MAX_LIMIT = 20;
export const PEOPLE_SUGGESTIONS_DEFAULT_LIMIT = 30;
export const PEOPLE_SUGGESTIONS_MAX_LIMIT = 40;

export type PeopleDiscoveryGroup = 'suggested' | 'following' | 'recent';
export const PEOPLE_SEARCH_CONTEXTS = [
  'global',
  'explore',
  'messages',
  'circle_invite',
  'witness_invite',
] as const;
export type PeopleSearchContext = (typeof PEOPLE_SEARCH_CONTEXTS)[number];
export type PeopleRelationshipStatus = 'self' | 'mutual' | 'following' | 'follows_viewer' | 'none';
export type PeopleResultReason =
  | 'current_user'
  | 'already_member'
  | 'already_invited'
  | 'blocked'
  | 'unavailable';

/**
 * The public, authenticated discovery shape shared by the API and its clients.
 * Keep this intentionally narrower than user_profiles: every value originates
 * from the privacy-filtered search_people_v2/suggest_people RPCs.
 */
export type PeopleDiscoveryPerson = {
  user_id: string | null;
  display_name: string;
  public_handle: string;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  role: 'individual' | 'institution';
  created_at: string | null;
  is_following: boolean;
  follows_viewer: boolean;
  mutual_follow_count: number;
  profile_path: string;
  is_current_user: boolean;
  discoverability_status: 'discoverable' | 'owner_only';
  relationship_status: PeopleRelationshipStatus;
  can_view_profile: boolean;
  can_follow: boolean;
  can_message: boolean;
  can_invite: boolean;
  can_witness: boolean;
  is_eligible: boolean;
  result_reason: PeopleResultReason | null;
  search_context: PeopleSearchContext;
  suggestion_reason?: string;
  discovery_group?: PeopleDiscoveryGroup;
  current_occupation?: string | null;
};

export type PeopleDiscoveryGroups = Record<PeopleDiscoveryGroup, PeopleDiscoveryPerson[]>;

export type PeopleSearchValidation =
  | { ok: true; query: string }
  | { ok: false; reason: 'too_short' | 'too_long' };

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nullableText(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text ? text.slice(0, maxLength) : null;
}

function nonNegativeInteger(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.min(Number.MAX_SAFE_INTEGER, Math.floor(number));
}

export function normalizePeopleSearchQuery(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ').replace(/^@+/, '').trim().toLowerCase();
}

export function parsePeopleSearchContext(value: unknown): PeopleSearchContext | null {
  if (typeof value !== 'string' || !value.trim()) return 'global';
  const context = value.trim().toLowerCase();
  return PEOPLE_SEARCH_CONTEXTS.includes(context as PeopleSearchContext)
    ? (context as PeopleSearchContext)
    : null;
}

export function validatePeopleSearchQuery(value: unknown): PeopleSearchValidation {
  const query = normalizePeopleSearchQuery(value);
  if (query.length < PEOPLE_SEARCH_QUERY_MIN) {
    return { ok: false, reason: 'too_short' };
  }
  if (query.length > PEOPLE_SEARCH_QUERY_MAX) {
    return { ok: false, reason: 'too_long' };
  }
  return { ok: true, query };
}

export function parseBoundedInteger(
  value: string | null,
  fallback: number,
  minimum: number,
  maximum: number
): number {
  if (!value || !/^\d+$/.test(value)) return fallback;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) return fallback;
  return Math.min(maximum, Math.max(minimum, parsed));
}

export function canonicalPeopleProfilePath(publicHandle: unknown): string | null {
  if (typeof publicHandle !== 'string') return null;
  const handle = publicHandle.trim().replace(/^@+/, '');
  if (!handle || handle.length > 64 || /[\s/\\?#]/.test(handle)) return null;
  return `/u/${encodeURIComponent(handle)}`;
}

/**
 * Converts an RPC/API row into the approved client shape. The public handle is
 * the stable browser-visible identity; the opaque user ID is retained only for
 * result metadata and private fields are dropped.
 */
export function mapPeopleDiscoveryRow(value: unknown): PeopleDiscoveryPerson | null {
  if (!isRecord(value)) return null;

  const publicHandle = nullableText(value.public_handle, 64)?.replace(/^@+/, '') ?? null;
  const profilePath = canonicalPeopleProfilePath(publicHandle);
  if (!publicHandle || !profilePath) return null;

  const displayName = nullableText(value.display_name, 120) ?? 'Member';
  const role = value.role === 'institution' ? 'institution' : 'individual';
  const createdAt = nullableText(value.created_at, 64);
  const group =
    value.discovery_group === 'suggested' ||
    value.discovery_group === 'following' ||
    value.discovery_group === 'recent'
      ? value.discovery_group
      : undefined;
  const suggestionReason = nullableText(value.suggestion_reason, 120) ?? undefined;
  const isCurrentUser = value.is_current_user === true;
  const relationshipStatus: PeopleRelationshipStatus =
    value.relationship_status === 'self' ||
    value.relationship_status === 'mutual' ||
    value.relationship_status === 'following' ||
    value.relationship_status === 'follows_viewer'
      ? value.relationship_status
      : 'none';
  const resultReason: PeopleResultReason | null =
    value.result_reason === 'current_user' ||
    value.result_reason === 'already_member' ||
    value.result_reason === 'already_invited' ||
    value.result_reason === 'blocked' ||
    value.result_reason === 'unavailable'
      ? value.result_reason
      : null;
  const searchContext = parsePeopleSearchContext(value.search_context) ?? 'global';

  return {
    user_id: nullableText(value.user_id, 64),
    display_name: displayName,
    public_handle: publicHandle,
    avatar_url: nullableText(value.avatar_url, 2048),
    bio: nullableText(value.bio, 160),
    location: nullableText(value.location, 120),
    role,
    created_at: createdAt,
    is_following: value.is_following === true,
    follows_viewer: value.follows_viewer === true,
    mutual_follow_count: nonNegativeInteger(value.mutual_follow_count),
    profile_path: profilePath,
    is_current_user: isCurrentUser,
    discoverability_status:
      value.discoverability_status === 'owner_only' ? 'owner_only' : 'discoverable',
    relationship_status: isCurrentUser ? 'self' : relationshipStatus,
    can_view_profile: value.can_view_profile !== false,
    can_follow: !isCurrentUser && value.can_follow !== false,
    can_message: !isCurrentUser && value.can_message === true,
    can_invite: !isCurrentUser && value.can_invite === true,
    can_witness: !isCurrentUser && value.can_witness === true,
    is_eligible: value.is_eligible !== false,
    result_reason: isCurrentUser ? 'current_user' : resultReason,
    search_context: searchContext,
    ...(suggestionReason ? { suggestion_reason: suggestionReason } : {}),
    ...(group ? { discovery_group: group } : {}),
  };
}

export function mapPeopleDiscoveryRows(value: unknown): PeopleDiscoveryPerson[] {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const people: PeopleDiscoveryPerson[] = [];
  for (const row of value) {
    const person = mapPeopleDiscoveryRow(row);
    if (!person || seen.has(person.public_handle)) continue;
    seen.add(person.public_handle);
    people.push(person);
  }
  return people;
}

export function readPeopleDiscoveryResponse(value: unknown): PeopleDiscoveryPerson[] {
  if (!isRecord(value)) return [];
  return mapPeopleDiscoveryRows(value.results);
}

export function groupPeopleByDiscovery(
  people: readonly PeopleDiscoveryPerson[]
): PeopleDiscoveryGroups {
  const groups: PeopleDiscoveryGroups = {
    suggested: [],
    following: [],
    recent: [],
  };

  for (const person of people) {
    if (person.discovery_group) groups[person.discovery_group].push(person);
  }
  return groups;
}

type RateLimitBucket = {
  count: number;
  resetAt: number;
};

type PeopleDiscoveryGlobal = typeof globalThis & {
  __ertzyxPeopleDiscoveryRateLimits?: Map<string, RateLimitBucket>;
};

const rateLimitStore =
  (globalThis as PeopleDiscoveryGlobal).__ertzyxPeopleDiscoveryRateLimits ??
  new Map<string, RateLimitBucket>();

(globalThis as PeopleDiscoveryGlobal).__ertzyxPeopleDiscoveryRateLimits = rateLimitStore;

export type PeopleDiscoveryRateLimitResult = {
  allowed: boolean;
  remaining: number;
  retryAfterSeconds: number;
};

/** Fixed-window, per-runtime limiter. Database authorization remains primary. */
export function consumePeopleDiscoveryRateLimit(
  key: string,
  options: { limit: number; windowMs: number; now?: number }
): PeopleDiscoveryRateLimitResult {
  const now = options.now ?? Date.now();
  const limit = Math.max(1, Math.floor(options.limit));
  const windowMs = Math.max(1_000, Math.floor(options.windowMs));

  if (rateLimitStore.size > 2_000) {
    for (const [bucketKey, bucket] of rateLimitStore) {
      if (bucket.resetAt <= now) rateLimitStore.delete(bucketKey);
    }
  }

  const current = rateLimitStore.get(key);
  if (!current || current.resetAt <= now) {
    rateLimitStore.set(key, { count: 1, resetAt: now + windowMs });
    return {
      allowed: true,
      remaining: limit - 1,
      retryAfterSeconds: 0,
    };
  }

  if (current.count >= limit) {
    return {
      allowed: false,
      remaining: 0,
      retryAfterSeconds: Math.max(1, Math.ceil((current.resetAt - now) / 1_000)),
    };
  }

  current.count += 1;
  return {
    allowed: true,
    remaining: Math.max(0, limit - current.count),
    retryAfterSeconds: 0,
  };
}

export function clearPeopleDiscoveryRateLimitsForTests(): void {
  rateLimitStore.clear();
}
